/**
 * Second opinion for a "not found" profile answer.
 *
 * The provider chain stops at the first provider that answers `false` (account
 * does not exist): only a THROW moves it to the next provider. A wrong `false`
 * from the primary (stale index, partial outage that answers 404, a provider
 * that cannot read some account types) would therefore block a real account at
 * setup and checkout without the backups ever being asked. Checkout callers run
 * this before blocking: the first backup that returns a profile wins; when no
 * backup finds it (each said `false` or failed), the primary's "not found"
 * stands.
 */
export interface BackupProfileProvider {
    name: string
    fetchProfile: () => Promise<unknown>
}

export interface BackupProfileResult {
    profile: unknown | false
    /** Name of the backup that found the profile, when one did. */
    foundBy: string | null
    /** Backups that failed (threw) rather than answering. */
    failed: string[]
}

export function isUsableProfile(value: unknown): boolean {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false
    if ((value as { error?: unknown }).error) return false
    return true
}

/**
 * Per-backup time limit. UpleapAPI was measured hanging past the 25 s edge
 * limit on a handle that does not exist (2026-10-27), which turned setup's
 * "not found" into a 504; a backup that is this slow counts as failed.
 */
export const BACKUP_PROFILE_TIMEOUT_MS = 7000

function withTimeout<T>(promise: Promise<T>, ms: number, name: string): Promise<T> {
    let timer: ReturnType<typeof setTimeout> | undefined
    const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`${name} timed out after ${ms} ms`)), ms)
    })
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
}

export async function confirmNotFoundWithBackups(backups: BackupProfileProvider[], timeoutMs = BACKUP_PROFILE_TIMEOUT_MS): Promise<BackupProfileResult> {
    const failed: string[] = []
    for (const backup of backups) {
        try {
            const profile = await withTimeout(Promise.resolve().then(backup.fetchProfile), timeoutMs, backup.name)
            if (isUsableProfile(profile)) return { profile, foundBy: backup.name, failed }
        } catch (error) {
            failed.push(backup.name)
            console.warn(`[confirmNotFoundWithBackups] ${backup.name} failed:`, error instanceof Error ? error.message : error)
        }
    }
    return { profile: false, foundBy: null, failed }
}
